import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, Building2, MapPin } from "lucide-react";

const STATUSES = ["Active", "Upcoming", "In Negotiation", "Inactive"];

const VenueDialog = ({ onSaved, trigger }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    name: "", location: "", address: "", status: "Upcoming", partner: "",
    contact_name: "", phone: "", email: "", start_date: "", notes: "", sports: [],
  });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    try {
      await api.post("/venues", f);
      toast.success("Venue created");
      setOpen(false); onSaved && onSaved();
      setF({ ...f, name: "", location: "", address: "" });
    } catch { toast.error("Failed to create venue"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>New Venue</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input data-testid="venue-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><Label>Location</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></div>
          <div><Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Address</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
          <div><Label>Partner</Label><Input value={f.partner} onChange={(e) => setF({ ...f, partner: e.target.value })} /></div>
          <div><Label>Contact Name</Label><Input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><Label>Start Date</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="venue-save-button" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Venues() {
  const [venues, setVenues] = useState([]);
  const load = async () => {
    const { data } = await api.get("/venues");
    setVenues(data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="venues-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Venues</div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>All Venues</h1>
        </div>
        <VenueDialog onSaved={load} trigger={<Button data-testid="new-venue-button" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> New Venue</Button>} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {venues.map((v) => (
          <Link key={v.id} to={`/venues/${v.id}`} data-testid={`venue-card-${v.id}`}
                className="block bg-white border border-slate-200 rounded-md p-5 hover:shadow-md hover:-translate-y-0.5 transition-transform">
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center">
                <Building2 className="h-5 w-5" />
              </div>
              <StatusBadge status={v.status} />
            </div>
            <div className="mt-3">
              <div className="text-lg font-bold text-slate-900">{v.name}</div>
              <div className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                <MapPin className="h-3 w-3" /> {v.location}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-1">
              {(v.sports || []).map((s, i) => (
                <span key={i} className="text-xs bg-slate-100 text-slate-700 rounded px-2 py-0.5 border border-slate-200">
                  {s.sport} · {s.courts}
                </span>
              ))}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
